import { connect } from "react-redux";
import React, { useEffect, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import {
  getShopifyConnectionLabel,
  isShopifyConnected,
  isShopifyConnectionButtonDisabled,
  isSpinnerEnabled,
  isAdminLogin,
  customToast,
} from "../../../shared/utility";

import Topbar from "../../../shared/components/topbar/Topbar";
import Button from "../../../shared/components/Buttons/Button";
import { SelectDropDown } from "../../../shared/components";
import { ReactComponent as ShopifyLogo } from "../../../assets/images/shopifyLogo.svg";
import {
  storeActions,
  systemActions,
  shopifyActions,
} from "../../../redux/actions";
import {
  buttonNameConstants,
  toastMessages,
  toastType,
  shopifyConstants
} from "../../../constants";
import { storeService } from "../../../services";
import ShopifyCustomerSyncing from "../../../shared/components/shopifyCustomerSyncing/ShopifyCustomerSyncing";
import CustomBodyModal from "../../../shared/components/modal/CustomBodyModal";
import ConfirmationModal from "../../Inventory/Items/ConfirmationModal/ConfirmationModal";
import ModalActionButtons from "../../../shared/components/modal/ModalActionButtons";

const ShopifyConnection = (props) => {
  const {
    currentStore,
    eligibleShopifyLocations,
    spinnerArray,
    user,
    getEligibleShopifyLocations,
    migrateShopifyLocation,
    getUpdatedCurrentStoreDetails,
    currentStoreCompany,
  } = props;

  const isConnected = isShopifyConnected(currentStore);
  const [showSelectLocationModal, setShowSelectLocationModal] = useState(false);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const isLocationsLoading = isSpinnerEnabled(
    spinnerArray,
    shopifyConstants.GET_ELIGIBLE_SHOPIFY_LOCATIONS
  );

  const eligibleLocationOptions = eligibleShopifyLocations.map((location) => ({
    label: location.name,
    value: location.id,
  }));

  const [isLoading, setIsLoading] = useState(false);
  const handleConnectShopify = async () => {
    try {
      if (isConnected) {
        setIsLoading(true);
        let res = await storeService.connectShopify({
          shop: currentStore.shopify.shop,
          storeId: currentStore.id,
        });
        if (res) {
          window.location.replace(res);
        } else {
          customToast(toastMessages.SOMETHING_WENT_WRONG, toastType.ERROR);
        }
      } else {
        window.location.replace(process.env.REACT_APP_SHOPIFY_APP_INSTALL_URL);
      }
    } catch (e) {
      customToast(toastMessages.SOMETHING_WENT_WRONG, toastType.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenSelectLocationModal = () => {
    setSelectedLocation(null);
    getEligibleShopifyLocations(currentStore.id);
    setShowSelectLocationModal(true);
  };

  const handleCloseSelectLocationModal = () => {
    setShowSelectLocationModal(false);
    setSelectedLocation(null);
  };

  const handleSubmitLocationSelection = (e) => {
    e.preventDefault();
    if (!selectedLocation?.value) return;
    setShowEditLocationModal(true);
  };

  const toggleEditLocationModal = () => {
    setShowEditLocationModal((prevState) => !prevState);
  };

  const handleConfirmNoButtonClick = () => {
    setShowEditLocationModal(false);
    setSelectedLocation(null);
  };

  const handleConfirmYesButtonClick = () => {
    migrateShopifyLocation(
      currentStore.id,
      selectedLocation.value,
      currentStore.companyId
    );
    setShowEditLocationModal(false);
    setShowSelectLocationModal(false);
    setSelectedLocation(null);
  };

  useEffect(() => {
    if (currentStore?.id) {
      getUpdatedCurrentStoreDetails(currentStore.id);
    }
    // eslint-disable-next-line
  }, []);

  const shopifyLocationName =
    currentStore?.shopify?.location?.shopifyLocationName;
  const shopifyLocationId = currentStore?.shopify?.location?.shopifyLocationId;

  return (
    <>
      <Topbar title="Shopify Connection"></Topbar>
      <Row className=" d-flex justify-content-center align-items-center">
        <Col md={11}>
          <Col
            md={5}
            className="mx-auto d-flex flex-column gap-3 billing-card-wrapper"
          >
            <ShopifyLogo className="mx-auto" width={128} />
            <Button
              type="button"
              className={"w-100 mt-0"}
              handleClick={handleConnectShopify}
              isDisabled={isShopifyConnectionButtonDisabled(
                currentStore,
                isLoading
              )}
              label={getShopifyConnectionLabel(currentStore)}
            />

            {isConnected && (
              <>
                <div className="w-100 text-start d-flex flex-column gap-2">
                  <p className="mb-0">
                    <strong>Connected Shopify Store: </strong>
                    {currentStore?.shopify?.shop}
                  </p>
                  <p className="mb-0">
                    <strong>Connected Shopify Location: </strong>
                    {shopifyLocationName}
                  </p>
                  <p className="mb-0">
                    <strong>Shopify Location ID: </strong>
                    {shopifyLocationId}
                  </p>
                </div>
                {isAdminLogin(user) && !currentStoreCompany?.isDummyCompany && (
                  <Button
                    type="button"
                    className={"w-100 mt-0"}
                    handleClick={handleOpenSelectLocationModal}
                    label={"Edit Connected Location"}
                  />
                )}
              </>
            )}

            <p className="w-100 mb-0">
              All Customers will be synced automatically, and Inventory Items
              will need to be synced selectively.
            </p>

            {isConnected && <ShopifyCustomerSyncing />}
          </Col>
        </Col>
      </Row>

      <CustomBodyModal
        show={showSelectLocationModal}
        onHide={handleCloseSelectLocationModal}
        title="Select Shopify Location"
        size="md"
      >
        <Form
          className="d-flex flex-column gap-3 p-2"
          onSubmit={handleSubmitLocationSelection}
        >
          <SelectDropDown
            label="Shopify Location"
            placeHolder={
              isLocationsLoading
                ? "Loading locations..."
                : "Select a Shopify Location"
            }
            options={eligibleLocationOptions}
            value={selectedLocation}
            handleChange={(option) => setSelectedLocation(option)}
            isSearchable={true}
            menuPlacement="bottom"
            isDisabled={isLocationsLoading}
          />
          <ModalActionButtons
            handleCloseModal={handleCloseSelectLocationModal}
            submitButtonLabel={buttonNameConstants.SUBMIT}
          />
        </Form>
      </CustomBodyModal>

      <ConfirmationModal
        isConfirmationModal={showEditLocationModal}
        handleNoButtonClick={handleConfirmNoButtonClick}
        handleYesButtonClick={handleConfirmYesButtonClick}
        toggleConfirmationModal={toggleEditLocationModal}
        title="Confirm edit shopify location"
        message={`
                 <ul>
                     <li>Inventory will automatically be transferred to the new Shopify Location.</li>
                     <li>The migration may take a significant amount of time depending on inventory size.</li>
                     <li>Inventory synchronization will be temporarily paused during the migration.</li>
                     <li>This operation should only be performed outside normal business operating hours.</li>
                     <li>The operation should not be interrupted once started.</li>
                 </ul>
     
               `}
        yesButtonText="Confirm"
        noButtonText="Cancel"
        isHtmlMessage={true}
      />
    </>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
  company: state.authentication.company,
  currentStoreCompany: state.company.currentStoreCompany,
  user: state.authentication.user,
  eligibleShopifyLocations: state.shopify.eligibleShopifyLocations,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  activateSpinner: (data) => dispatch(systemActions.activateSpinner(data)),
  deactivateSpinner: (data) => dispatch(systemActions.deactivateSpinner(data)),
  getUpdatedCurrentStoreDetails: (storeId) =>
    dispatch(storeActions.getUpdatedCurrentStoreDetails(storeId)),
  getEligibleShopifyLocations: (storeId) =>
    dispatch(shopifyActions.getEligibleShopifyLocations(storeId)),
  migrateShopifyLocation: (storeId, locationId, companyId) =>
    dispatch(
      shopifyActions.migrateShopifyLocation(storeId, locationId, companyId)
    ),
});

//-------Export AddNewUser Component
export default connect(mapStateToProps, mapDispatchToProps)(ShopifyConnection);
